import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo } from 'react';
import { Drawer } from '../../../../lib/components/atomic/dataDisplay/drawer';
import { InfoSection } from '../../../../lib/components/atomic/typography/InfoSection';
import { useTranslation } from 'react-i18next';
import { useCreateBonus } from '../hooks/useCreateBonus';
import { useStepper } from '../../partials/actionStepperPartial';
import { ArrowForwardIconSvg } from '../../../../lib/icons';
import { SpecialOccasionTypeEnum, TaxTreatmentEnum } from '@pxtra-client-lib-legacy/gen';
import { SelectionCard } from '../../../../lib/components/atomic/dataDisplay/selectionCard';
import { FeedbackAlert } from '../../../../lib/components/atomic/feedback';
import { FormControl } from '@mui/material';
import { Select, Switch } from '../../../../lib/components';
import { PARTIAL_SPECIAL_OCCASION_TYPES, TAX_TREATMENTS } from '../store';
import Util from '../../../../misc/util';
import { cn } from '../../../../lib/util';
import { useStore } from '../../../../hooks/useStore';
import { useAvailableTaxTreatments } from '../../../../queries/bonus';
import { HintBox } from '../../../../lib/components/atomic/typography/hintBox';
import { useFormik } from 'formik';
import { TAX_TREATMENT_MAX_AMOUNT, taxTreatmentSchema } from '../utils/validationSchemas';

export const TaxTreatment = observer(() => {
  const { t } = useTranslation();
  const { newBonus } = useCreateBonus();
  const { setNextButtonConfig, activeStep, setActiveStep, showInfoDrawer, setShowInfoDrawer } =
    useStepper();
  const selectOptions = PARTIAL_SPECIAL_OCCASION_TYPES.map((type) => ({
    value: type,
    text: t(`bonus:${type}`)
  }));

  const { companyStore } = useStore();
  const companyId = companyStore.company?.id ?? 'MISSING_ID';
  const { data: availableTaxTreatments } = useAvailableTaxTreatments(companyId);

  const isReliefPremiumEnabled = companyStore.settings?.relief_premium_enabled === true;
  const visibleTaxTreatments = isReliefPremiumEnabled
    ? TAX_TREATMENTS
    : TAX_TREATMENTS.filter((t) => t !== 'RELIEF_PREMIUM_2026');

  const { values, errors, touched, setFieldValue, submitForm, setValues, setTouched } = useFormik({
    initialValues: {
      taxTreatment: newBonus.taxTreatment,
      specialOccasionType: newBonus.specialOccasionType
    },
    enableReinitialize: true,
    validationSchema: taxTreatmentSchema(),
    onSubmit: (values) => {
      newBonus.taxTreatment = values.taxTreatment as TaxTreatmentEnum;
      newBonus.specialOccasionType = values.specialOccasionType as SpecialOccasionTypeEnum;
      setActiveStep(activeStep + 1);
    }
  });

  const updateTaxTreatment = useCallback(
    (taxTreatment: TaxTreatmentEnum) => {
      setTouched({
        taxTreatment: true,
        specialOccasionType: false
      });
      setValues({
        taxTreatment,
        specialOccasionType: undefined
      });
      newBonus.resetPayoutType();
    },
    [setTouched, setValues, newBonus]
  );

  const updateSpecialOccasionType = useCallback(
    (type: SpecialOccasionTypeEnum) => {
      setFieldValue('specialOccasionType', type);
      newBonus.resetPayoutType();
    },
    [setFieldValue, newBonus]
  );

  const nextButtonConfig = useMemo(
    () => ({
      text: t('common:NEXT'),
      endIcon: <ArrowForwardIconSvg />,
      onClick: () => {
        submitForm();
      }
    }),
    [t, submitForm]
  );

  useEffect(() => {
    setNextButtonConfig(nextButtonConfig);
  }, [nextButtonConfig, setNextButtonConfig]);

  return (
    <>
      <div className="flex flex-col">
        <div className="-mt-2 mb-2 flex flex-col gap-y-8">
          {(errors.taxTreatment || (errors.specialOccasionType && touched.specialOccasionType)) && (
            <FeedbackAlert description={t('bonus:error:taxTreatmentMissing')} severity="error" />
          )}
          {values.taxTreatment === 'RELIEF_PREMIUM_2026' && (
            <FeedbackAlert
              title={t('bonus:alerts:specialOccasionTaxHintTitle')}
              description={t('bonus:alerts:reliefPremiumHintDescription')}
              severity="warning"
            />
          )}
          <div>{t('bonus:subtitle:taxTreatment')}</div>
        </div>
        <div className="w-full flex flex-col gap-y-4 mt-6">
          {availableTaxTreatments &&
            visibleTaxTreatments.map((taxTreatment) => (
              <div key={taxTreatment}>
                <SelectionCard
                  key={taxTreatment}
                  item={taxTreatment}
                  badge={
                    taxTreatment !== 'FULLY_TAXED'
                      ? t('common:max') +
                        Util.formatCurrency(TAX_TREATMENT_MAX_AMOUNT[taxTreatment], false)
                      : undefined
                  }
                  isSelected={values.taxTreatment === taxTreatment}
                  disabled={!availableTaxTreatments.includes(taxTreatment)}
                  tooltip={
                    taxTreatment === 'NON_CASH_BENEFIT' &&
                    !availableTaxTreatments.includes(taxTreatment)
                      ? t('bonus:nonCashBenefitDisabled')
                      : ''
                  }
                  onSelect={(e) => updateTaxTreatment(e as TaxTreatmentEnum)}
                />
                {taxTreatment === 'SPECIAL_OCCASION' &&
                  values.taxTreatment === 'SPECIAL_OCCASION' && (
                    <div className="flex gap-x-4 py-4 mb-2">
                      <FormControl
                        className={cn(
                          'w-1/3 rounded-lg bg-grey-50 h-12',
                          errors.specialOccasionType &&
                            touched.specialOccasionType &&
                            'border-2 border-solid border-error-main'
                        )}
                      >
                        <Switch
                          label={t('bonus:BIRTHDAY')}
                          name="birthday"
                          checked={values.specialOccasionType === 'BIRTHDAY'}
                          helperText={t('bonus:oneTimeRecurring')}
                          onChange={() => updateSpecialOccasionType('BIRTHDAY')}
                        />
                      </FormControl>
                      <FormControl
                        className={cn(
                          'w-1/3 rounded-lg bg-grey-50 h-12',
                          errors.specialOccasionType &&
                            touched.specialOccasionType &&
                            'border-2 border-solid border-error-main'
                        )}
                      >
                        <Switch
                          label={t('bonus:ANNIVERSARY')}
                          name="anniversary"
                          checked={values.specialOccasionType === 'ANNIVERSARY'}
                          helperText={t('bonus:oneTimeRecurring')}
                          onChange={() => updateSpecialOccasionType('ANNIVERSARY')}
                        />
                      </FormControl>
                      <FormControl
                        className={cn(
                          'w-1/3 rounded-lg bg-grey-50 h-12 text-grey-600',
                          errors.specialOccasionType &&
                            touched.specialOccasionType &&
                            'border-2 border-solid border-error-main'
                        )}
                      >
                        <Select
                          options={selectOptions}
                          placeholder={t('bonus:otherOccasion')}
                          value={
                            values.specialOccasionType &&
                            selectOptions
                              .map((option) => option.value)
                              .includes(values.specialOccasionType as SpecialOccasionTypeEnum)
                              ? values.specialOccasionType
                              : undefined
                          }
                          helperText={t('bonus:oneTimeOnly')}
                          onChange={(e) =>
                            updateSpecialOccasionType(e.target.value as SpecialOccasionTypeEnum)
                          }
                        />
                      </FormControl>
                    </div>
                  )}
              </div>
            ))}
        </div>
      </div>
      <Drawer
        label={t('bonus:taxTreatmentDrawer:title')}
        open={showInfoDrawer}
        closeDrawer={() => setShowInfoDrawer(false)}
        sx={{ width: '1200px' }}
      >
        <div className="flex flex-col gap-y-6">
          <InfoSection
            content={t(
              isReliefPremiumEnabled
                ? 'bonus:taxTreatmentDrawer:textsWithReliefPremium'
                : 'bonus:taxTreatmentDrawer:texts',
              { returnObjects: true }
            )}
          />
          <HintBox description={t('bonus:taxTreatmentDrawer:hint')} />
        </div>
      </Drawer>
    </>
  );
});
